// client/src/components/common/PickerSurface.tsx
//
// Where a shared picker's content opens (ST-00294): the bottom sheet on a phone or a touch
// screen (PickerSheet) or the popup anchored to the field on a desktop (PickerPopover). Both take
// the same props, so each picker renders one content tree for both. The variant is passed down
// on context for the few pieces that lay out differently (the presets list, the calendar's
// months); sizes differ by CSS only (PickerPopover.css compacts what sits inside .picker-pop).

import { createContext, useContext } from 'react';
import PickerPopover from './PickerPopover';
import PickerSheet, { type PickerSheetProps } from './PickerSheet';

export type PickerVariant = 'sheet' | 'popover';

const PickerVariantContext = createContext<PickerVariant>('sheet');

/** The container the content is in: 'sheet' (phone / touch) or 'popover' (desktop). */
export const usePickerVariant = (): PickerVariant => useContext(PickerVariantContext);

export interface PickerSurfaceProps extends PickerSheetProps {
  /** From usePickerSheetMode in the picker: true → 'sheet'. */
  variant: PickerVariant;
}

export default function PickerSurface({ variant, ...props }: PickerSurfaceProps) {
  return (
    <PickerVariantContext.Provider value={variant}>
      {variant === 'popover' ? <PickerPopover {...props} /> : <PickerSheet {...props} />}
    </PickerVariantContext.Provider>
  );
}
